import { Role, rolesManipulator } from 'auth';
import dayjs from 'dayjs';
import { FormikProvider, useFormik } from 'formik';
import { delay } from 'lodash';
import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import IncidentTypeSelectPaginate from '@/modules/common/components/IncidentTypeSelectPaginate.tsx';
import { IncidentReportingState } from '@/modules/common/pages/IncidentReporting.tsx';
import { commonPaths } from '@/modules/common/routes.tsx';
import ClientsSelectPaginated from '@/modules/admin-dashboard/components/ClientsSelectPaginated.tsx';
import {
  BaseModalProps,
  Button,
  CalendarInput,
  CalendarInputProps,
  ModalContentWrapper,
  SelectOption,
  SelectPaginateOption,
  TextInputField,
} from 'ui';
import { useCloseParentModal } from 'ui/hooks';
import { getDisableTime, getDisabledDate } from 'ui/utils';

export type IncidentOptionValue = { id: number; form_id: number; departmentName: string } | null;

const initialValues: IncidentReportingState & {
  incidentOption?: SelectOption<IncidentOptionValue>;
  clientOption?: SelectPaginateOption<number>;
} = {
  departmentName: '',
  form_id: 0,
  name: '',
  incident_id: 0,
  incidentOption: undefined,
  date_occurred: dayjs().format('YYYY-MM-DDTHH:mm'),
  client_id: undefined,
  clientOption: undefined,
};

const validationSchema = yup.object().shape({
  name: yup.string().trim().min(3).max(50).required('Name is required'),
  incident_id: yup.number().min(1, 'Type is required').required('Type is required'),
  date_occurred: yup.string().required('Date is required'),
});

interface CreateIncidentModalProps extends BaseModalProps {
  clientId?: number;
}

const CreateIncidentModal = ({ open, onClose, clientId }: CreateIncidentModalProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isUserAdmin = rolesManipulator.current_user_role === Role.COMPLIANCE_ADMIN;

  const formik = useFormik({
    initialValues: {
      ...initialValues,
      client_id: clientId,
    },
    validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async state => {
      navigate(
        { pathname: commonPaths.static.incidentReporting, search: location.search },
        { state: { ...state, returnPath: location.pathname + location.search } },
      );
      onClose();
    },
  });

  const handleSelectType = useCallback(
    async (newValue: SelectPaginateOption<IncidentOptionValue>) => {
      if (!newValue.value) return;

      await formik.setFieldValue('incident_id', newValue.value.id);
      await formik.setFieldValue('form_id', newValue.value.form_id);
      await formik.setFieldValue('departmentName', newValue.value.departmentName);
      await formik.setFieldValue('incidentOption', newValue);
      delay(formik.validateField, 1, 'incident_id');
    },
    [formik],
  );

  const handleSelectClient = useCallback(
    (newValue: SelectPaginateOption<number> | null) => {
      formik.setFieldValue('client_id', newValue?.value || undefined);
      formik.setFieldValue('clientOption', newValue);
    },
    [formik],
  );

  const handleChangeDate: CalendarInputProps['onChange'] = date => {
    formik.setFieldError('date_occurred', '');
    if (date) {
      const date_occurred = dayjs(date).format('YYYY-MM-DDTHH:mm');
      formik.setFieldValue('date_occurred', date_occurred);
      return;
    }
    formik.setFieldValue('date_occurred', null);
  };

  return (
    <ModalContentWrapper title="New event" open={open} onClose={useCloseParentModal(onClose)}>
      <FormikProvider value={formik}>
        <div className="flex flex-col gap-8">
          <IncidentTypeSelectPaginate<IncidentOptionValue>
            enableCreation={isUserAdmin}
            label="Select event type"
            isMulti={false}
            value={formik.values.incidentOption}
            onChange={handleSelectType}
            error={formik.errors.incident_id}
            valueMapper={({ id, form_id, department }) => ({
              id,
              form_id,
              departmentName: department?.name || '',
            })}
          />
          <TextInputField name="name" label="Event name" placeholder="Name..." />
          {!clientId && (
            <ClientsSelectPaginated
              isMulti={false}
              value={formik.values.clientOption}
              onChange={handleSelectClient}
              isClearable={true}
            />
          )}
          <CalendarInput
            showTime
            format="DD-MM-YYYY, HH:mm"
            label="Datte occured"
            clearIcon=""
            disabledDate={getDisabledDate}
            disabledTime={getDisableTime}
            value={dayjs(formik.values.date_occurred)}
            error={formik.errors.date_occurred}
            onChange={handleChangeDate}
          />
          <Button onClick={formik.submitForm}>Go to form</Button>
        </div>
      </FormikProvider>
    </ModalContentWrapper>
  );
};

export default CreateIncidentModal;
